import { CapacitorPassToWallet } from 'capacitor-pass-to-wallet';
import { Browser } from '@capacitor/browser';
import { getApiUrl, isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';
import { QR_WALLET_TOAST_SCOPE } from '@/store/modules/toast';

const FETCH_TIMEOUT_MS = 30000;

export default {
  data() {
    return {
      isAddingToWallet: false,
    };
  },

  computed: {
    // Apple Wallet is only available via the native iOS "Add to Wallet" sheet.
    canAddToAppleWallet() {
      return isIosNativeOnly();
    },
    // Google Wallet save flow only runs on native Android.
    canAddToGoogleWallet() {
      return isAndroidNativeOnly();
    },
  },

  methods: {
    async onAppleWalletTap() {
      if (this.isAddingToWallet) return;
      this.isAddingToWallet = true;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      let stage = 'init';
      const url = `${getApiUrl()}/api/wallet/apple/pass`;

      try {
        console.log(
          '[wallet] requesting pass from',
          url,
          '— hasToken:',
          !!this.$store.state.user.token,
        );
        stage = 'fetch';
        const response = await fetch(url, {
          signal: controller.signal,
          cache: 'no-store',
          headers: { Authorization: `Bearer ${this.$store.state.user.token}` },
        });
        clearTimeout(timeoutId);
        console.log(
          '[wallet] response status:',
          response.status,
          response.headers.get('content-type'),
        );

        if (!response.ok) {
          const body = await response.text().catch(() => '');
          throw new Error(`Wallet pass request failed: ${response.status} ${body.slice(0, 300)}`);
        }

        stage = 'read-blob';
        const blob = await response.blob();
        console.log('[wallet] blob size:', blob.size, 'type:', blob.type);

        stage = 'to-base64';
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });

        // Hand the raw .pkpass bytes to PassKit's PKAddPassesViewController,
        // which presents the native "Add to Apple Wallet" sheet. (Opening the
        // file with a document viewer only shows a file preview, not the sheet.)
        stage = 'add-to-wallet';
        await CapacitorPassToWallet.addToWallet({ base64 });
        console.log('[wallet] addToWallet succeeded');
      } catch (e) {
        // Errors serialize to {} in some loggers (message/stack are non-enumerable),
        // so extract the useful fields explicitly.
        console.error(`Add to Apple Wallet failed at stage "${stage}":`, {
          name: e?.name,
          message: e?.message,
          code: e?.code,
          stack: e?.stack,
          raw: typeof e === 'string' ? e : undefined,
        });
        // The plugin rejects with {"code": 100, "message": "Pass already added"}
        // when the pass is already in Wallet — that's not an error, just inform.
        const raw = typeof e === 'string' ? e : e?.message || '';
        if (raw.includes('already added') || raw.includes('"code": 100')) {
          this.$store.dispatch(
            'toast/success',
            {
              message: 'This pass is already in your Apple Wallet.',
              action: { label: 'Open', vuexAction: 'toast/openAppleWallet' },
              scope: QR_WALLET_TOAST_SCOPE,
            },
            { root: true },
          );
        } else {
          this.$store.dispatch(
            'toast/error',
            {
              message: 'Could not add to Apple Wallet. Please try again.',
              scope: QR_WALLET_TOAST_SCOPE,
            },
            { root: true },
          );
        }
      } finally {
        clearTimeout(timeoutId);
        this.isAddingToWallet = false;
      }
    },

    async onGoogleWalletTap() {
      if (this.isAddingToWallet) return;
      this.isAddingToWallet = true;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      let stage = 'init';
      const url = `${getApiUrl()}/api/wallet/google/save`;

      try {
        console.log(
          '[wallet] requesting Google save url from',
          url,
          '— hasToken:',
          !!this.$store.state.user.token,
        );
        stage = 'fetch';
        const response = await fetch(url, {
          signal: controller.signal,
          cache: 'no-store',
          headers: { Authorization: `Bearer ${this.$store.state.user.token}` },
        });
        clearTimeout(timeoutId);
        console.log('[wallet] Google response status:', response.status);

        if (!response.ok) {
          const body = await response.text().catch(() => '');
          throw new Error(
            `Google save url request failed: ${response.status} ${body.slice(0, 300)}`,
          );
        }

        stage = 'parse';
        const { saveUrl } = await response.json();
        if (!saveUrl) {
          throw new Error('Google save url missing from response');
        }

        // Opening the save URL hands off to the Google Wallet native save flow.
        stage = 'open-browser';
        await Browser.open({ url: saveUrl });
        console.log('[wallet] Google save url opened');
      } catch (e) {
        // Errors serialize to {} in some loggers (message/stack are non-enumerable),
        // so extract the useful fields explicitly.
        console.error(`Save to Google Wallet failed at stage "${stage}":`, {
          name: e?.name,
          message: e?.message,
          code: e?.code,
          stack: e?.stack,
          raw: typeof e === 'string' ? e : undefined,
        });
        this.$store.dispatch(
          'toast/error',
          {
            message: 'Could not save to Google Wallet. Please try again.',
            scope: QR_WALLET_TOAST_SCOPE,
          },
          { root: true },
        );
      } finally {
        clearTimeout(timeoutId);
        this.isAddingToWallet = false;
      }
    },
  },
};
